<template>
    <fragment>
        <custom-search
            :label="label"
            :display-value="itemCurrent.name"
            :disabled="!enabled"
            placeholder="Buscador de productos"
            :fetch-items="searchProducts"
            :show-items-on-focus="showSuggestions"
            @select="onSelect"
            @focus="$emit('focus')"
            @blur="$emit('blur')"
        >
            <template #item="{ item }">
                <span class="flex items-center justify-between gap-2">
                    <span class="flex flex-1 justify-between">
                        <span>{{ item.name | cut(50) }}</span>
                        <span>{{ item.priceOffer | money }}</span>
                    </span>
                </span>
            </template>
            <template v-if="paginated" #footer>
                <div
                    class="flex flex-col gap-1 border-t border-theme-border p-2"
                >
                    <button
                        v-if="hasMore"
                        type="button"
                        class="w-full rounded px-3 py-2 text-center text-sm text-theme-primary hover:bg-theme-hover disabled:opacity-60"
                        :disabled="isLoadingMore"
                        @click="loadMore"
                    >
                        {{ isLoadingMore ? "Cargando..." : "Mostrar más" }}
                    </button>
                </div>
            </template>
        </custom-search>
    </fragment>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import CustomSearch from "@/components/utils/CustomSearch.vue";

const PAGE_SIZE = 12;

export default {
    name: "ProductsSearch",
    components: {
        CustomSearch
    },
    model: {
        prop: "itemCurrent",
        event: "input"
    },
    props: {
        defaultFilters: {
            default: () => ({}),
            type: Object,
            required: false
        },
        itemCurrent: {
            default: () => ({ name: "" }),
            type: Object,
            required: false
        },
        label: {
            default: () => "",
            type: String,
            required: false
        },
        enabled: {
            default: () => true,
            type: Boolean,
            required: false
        },
        // Antes de escribir nada el buscador no mostraba nada, así que el
        // usuario pensaba que no había productos. Con esto se sugieren los
        // más relevantes de una vez al enfocar el campo.
        showSuggestions: {
            default: () => false,
            type: Boolean,
            required: false
        },
        suggestionsLimit: {
            default: () => 6,
            type: Number,
            required: false
        },
        // Opt-in: sin este prop el componente conserva exactamente su
        // comportamiento anterior (listAllProducts, sin paginar, sin filtro
        // de activos por defecto) — lo siguen usando tal cual orders/product,
        // orders/new y ReferencesSearchScanner.
        paginated: {
            default: () => false,
            type: Boolean,
            required: false
        }
    },
    emits: ["blur", "focus", "input"],
    data: () => ({
        currentItems: [],
        currentPage: 1,
        currentKeyword: "",
        hasMore: false,
        isLoadingMore: false
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"])
    },
    methods: {
        ...mapActions("products", ["listAllProducts", "listProductsPaginated"]),
        async searchProducts(keyword, required = false) {
            if (!this.paginated) {
                const items = await this.listAllProducts({
                    ...this.defaultFilters,
                    keyword,
                    required
                });
                if (!keyword && this.showSuggestions) {
                    return items.slice(0, this.suggestionsLimit);
                }
                return items;
            }
            return this.fetchProductsPage({ keyword, page: 1, replace: true });
        },
        async fetchProductsPage({ keyword, page, replace }) {
            const { items, pagination } = await this.listProductsPaginated({
                active: true,
                ...this.defaultFilters,
                keyword,
                page,
                itemsByPage: PAGE_SIZE
            });
            this.currentKeyword = keyword;
            this.currentPage = page;
            if (replace) {
                this.currentItems = items;
            } else {
                this.currentItems.push(...items);
            }
            this.hasMore = Boolean(pagination && page < pagination.pagesNumber);
            return this.currentItems;
        },
        async loadMore() {
            if (!this.hasMore || this.isLoadingMore) return;
            this.isLoadingMore = true;
            try {
                await this.fetchProductsPage({
                    keyword: this.currentKeyword,
                    page: this.currentPage + 1,
                    replace: false
                });
                this.isLoadingMore = false;
            } catch (error) {
                this.isLoadingMore = false;
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        onSelect(item) {
            this.$emit("input", item);
        }
    }
};
</script>
